import { Component, EventEmitter, HostBinding, Input, Output, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { CandidateContactService } from 'src/app/core/services/candidate-contact.service';
import { GoogleAnalyticsService } from 'ngx-google-analytics';
import { GA_EVENTS, GA_PROPS } from 'src/app/core/const.variables';
import { USER_ROLES } from 'src/app/core/services/api.service';
import { SourcingType } from 'src/app/core/sourcing-type.enum';
import { getWindow } from 'ssr-window';
import { buildWhatsappUrl, getWhatsappPhoneNumber } from '../../utils/whatsapp-click-to-chat';
import { isSkillMatched } from '../../utils/skill-match';
import { TranslateService } from '@ngx-translate/core';
import { SalaryService } from '../../services/salary.service';
import { CandidateProfileEventType } from 'src/app/core/services/recruiter-activity-analytics.service.type';

@Component({
  selector: 'match-item',
  templateUrl: 'match-item.component.html',
  styleUrls: ['match-item.component.scss'],
})
export class MatchItemComponent implements OnInit, OnDestroy {
  revealingPhone = false;
  contactError = '';
  private contactRequest?: Subscription;
  private contactUpdates?: Subscription;

  ngOnInit(): void {
    this.contactUpdates = this.contacts.revealed$.subscribe(({ vacancyId, talentId, contact }) => {
      if (this.candidateId !== talentId) return;
      if (this.vacancyId && vacancyId && this.vacancyId !== vacancyId) return;
      
      if (this.item && this.item.talent) {
        if (contact.detail === 'email') {
          this.item.talent.email = contact.value;
          this.item.talent.isEmailViewed = true;
        } else {
          this.item.talent.phone = { countryCode: contact.countryCode, phoneNumber: contact.value };
          this.item.talent.isPhoneNumberViewed = true;
        }
      }
    });
  }

  ngOnDestroy(): void {
    this.contactRequest?.unsubscribe();
    this.contactUpdates?.unsubscribe();
  }

  readonly COLLAPSED_SKILLS_COUNT = 8;
  readonly USER_ROLES = USER_ROLES; 
  readonly SourcingType = SourcingType;
  readonly RecruiterActivityEventType = CandidateProfileEventType;
  private _item = null;
  candidateCompensationRows: { labelKey: string; shortLabelKey: string; value: string; isMissing: boolean; isUnverified: boolean }[] = [];
  isSummaryExpanded = false;
  areSkillsExpanded = false;
  @Input() role: string = USER_ROLES.talent;
  @Input() isOtherMatch = true;
  @Input() isActive = false;
  private _vacancySkillNames: Set<string> | null = null;
  // Candidate hard skills with vacancy matches moved to the front (original order otherwise kept).
  displaySkills: string[] = [];

  @Input() set item(value) {
    if (value && value.talent && this.isImpersonating) {
      value = { ...value, talent: { ...value.talent, isEmailViewed: true, isPhoneNumberViewed: true } };
    }
    this.contactRequest?.unsubscribe();
    this.showWhatsappPopup = false;
    this.revealingPhone = false;
    this.contactError = '';
    this._item = value;
    this.candidateCompensationRows = this.buildCandidateCompensationRows(value);
    this.isSummaryExpanded = false;
    this.areSkillsExpanded = false;
    this.displaySkills = this.buildDisplaySkills();
  }
  @Input() index = null;
  @Input() standAlone = true;
  @Input() showActions = false;
  @Input() validJobTitles = null;
  @Input() leastFoundSkills = [];
  @Input() displayAsScore = false;
  @Input() showExtraInfo = false;
  @Input() vacancyId: string = null;
  @Input() vacancyTitle: string = null;
  @Input() isProcessingAction = false;
  @Input() processingActionCandidateId: string = null;
  @Input() processingActionType: 'like' | 'archive' | 'unarchive' | 'download' | 'verify' | null = null;
  @Input() verificationMessage = '';
  @Input() uanVerificationEnabled = false;
  @Input() canManageOutreach = false;
  @Input() isLocationIndia = false;
  @Input() statusFilter: SourcingType | null = null;

  private get isImpersonating(): boolean {
    return typeof sessionStorage !== 'undefined' && !!sessionStorage.getItem('Atalef:Impersonate');
  }
  @Input() set vacancySkillNames(value: Set<string> | null) {
    this._vacancySkillNames = value;
    this.displaySkills = this.buildDisplaySkills();
  }

  get vacancySkillNames(): Set<string> | null {
    return this._vacancySkillNames;
  }
  @Output() onAnalyticsEvent = new EventEmitter<{eventType: CandidateProfileEventType, item: any}>();
  @Output() onApply = new EventEmitter();
  @Output() onExplore = new EventEmitter();
  @Output() onClick = new EventEmitter();
  @Output() onContact = new EventEmitter();
  @Output() onGoToVacancy = new EventEmitter();
  @Output() onLikeToggle = new EventEmitter<{ candidateId: string, isLiked: boolean }>();
  @Output() onArchive = new EventEmitter<string>();
  @Output() onUnarchive = new EventEmitter<string>();
  @Output() onDownloadResume = new EventEmitter<{ candidateId: string; candidateName?: string }>();
  @Output() onVerifyUan = new EventEmitter<string>();
  matchId = null;
  showSparkles = false;
  showConfetti = false;
  showWhatsappPopup = false;
  whatsappMessage = '';
  @HostBinding('class.has-whatsapp-popup') get hasWhatsappPopup(): boolean {
    return this.showWhatsappPopup;
  }

  constructor(
    private gaService: GoogleAnalyticsService,
    private translate: TranslateService,
    private salaryService: SalaryService,
    private contacts: CandidateContactService
  ) {
  }

  get sourcingType(): number | undefined {
    return this.item?.sourcing?.type;
  }

  get hasResume(): boolean {
    return !!this.item?.talent?.resumeUrl;
  }
  get item() {
    return this._item;
  }
  private get candidateId(): string | null {
    return this.item?.talent?.id || null;
  }

  /**
   * One-line education for the card. Prefers the flat degree/field (manual, Naukri, RocketReach);
   * falls back to the first parsed CV education (active/private uploads), whose description is
   * multi-line ("BS in X (1999)\nGPA ..."), so only its first line is used.
   */
  get educationSummary(): string | null {
    const talent = this.item?.talent;
    if (!talent) {
      return null;
    }

    const flat = [talent.educationDegree, talent.educationField].filter(Boolean).join(' · ');
    if (flat) {
      return flat;
    }

    const first = talent.educations?.[0];
    if (!first) {
      return null;
    }

    const headline = (first.description || '').split('\n').map((line: string) => line.trim()).find(Boolean);
    return [headline, first.issuingOrganization].filter(Boolean).join(', ') || null;
  }

  get hasLongSummary(): boolean {
    return (this.item?.talent?.summary || '').trim().length > 220;
  }

  get visibleSkillsCount(): number {
    return this.areSkillsExpanded
      ? (this.item?.talent?.hardSkills?.length || this.COLLAPSED_SKILLS_COUNT)
      : this.COLLAPSED_SKILLS_COUNT;
  }

  downloadResume(event: Event): void {
    event.stopPropagation();
    if (this.isProcessingAction) {
      return;
    }
    if (this.role === USER_ROLES.hm && this.item?.talent?.id) {
      this.onDownloadResume.emit({
        candidateId: this.item.talent.id,
        candidateName: this.item.talent.name
      });
    }
  }

  get uanVerification() {
    const verification = this.item?.talent?.verification;
    return verification?.uan?.status
      ? verification.uan
      : null;
  }

  get isVerificationInProgress(): boolean {
    const status = this.item?.talent?.verification?.status;
    return status === 'Queued' || status === 'InProgress';
  }

  // Backend flags candidates whose phone number cannot be used for a UAN lookup.
  get isUanNumberValid(): boolean {
    const canVerifyUan = this.item?.talent?.verification?.canVerifyUan
      ?? this.item?.verification?.canVerifyUan;

    return canVerifyUan !== false;
  }

  get shouldShowVerifyUan(): boolean {
    return (this.role === USER_ROLES.hm || this.role === USER_ROLES.admin)
      && !!this.candidateId
      && !this.isVerificationInProgress
      && (!this.uanVerification || this.canRetryUan);
  }

  get canVerifyUan(): boolean {
    return this.uanVerificationEnabled && this.isUanNumberValid && this.shouldShowVerifyUan;
  }

  get canRetryUan(): boolean {
    return this.uanVerification?.status === 'NotAvailable'
      && (this.uanVerification.reasons || []).some(reason =>
        reason === 'EmploymentHistoryUnavailable' || reason === 'VerificationCheckFailed');
  }

  get isVerificationCheckFailed(): boolean {
    return this.uanVerification?.status === 'NotAvailable'
      && (this.uanVerification.reasons || []).includes('VerificationCheckFailed');
  }

  getUanStatusKey(status: string): string {
    return status ? `EmploymentVerification.Statuses.${status}` : '';
  }

  getUanStatusClass(status: string): string {
    const classes = {
      Verified: 'verified',
      PartiallyVerified: 'partially-verified',
      NotVerified: 'not-verified',
      NotAvailable: 'not-available',
    };
    return classes[status] || '';
  }

  getUanReasonLabel(reason: string): string {
    if (!reason) {
      return '';
    }

    const key = `EmploymentVerification.Reasons.${reason}`;
    const label = this.translate.instant(key);
    return label === key ? reason : label;
  }

  get uniqueUanReasons(): string[] {
    return Array.from(new Set((this.uanVerification?.reasons || []).filter(Boolean)));
  }

  toggleSummary(event: Event): void {
    event.stopPropagation();
    this.isSummaryExpanded = !this.isSummaryExpanded;
  }

  isVacancySkill(skill: unknown): boolean {
    return isSkillMatched(this._vacancySkillNames, String(skill ?? ''));
  }

  private buildDisplaySkills(): string[] {
    const skills: string[] = this._item?.talent?.hardSkills || [];
    if (!this._vacancySkillNames?.size) {
      return skills;
    }
    const matched = skills.filter(skill => this.isVacancySkill(skill));
    const others = skills.filter(skill => !this.isVacancySkill(skill));
    return [...matched, ...others];
  }

  toggleSkills(event: Event): void {
    event.stopPropagation();
    this.areSkillsExpanded = !this.areSkillsExpanded;
  }

  isKnownSourcingType(type: number): boolean {
    return type === SourcingType.Active || type === SourcingType.External || type === SourcingType.Passive;
  }

  getStatusClass(type: number): string {
    switch(type) {
      case SourcingType.Active: return 'active';
      case SourcingType.External: return 'passive';
      case SourcingType.Passive: return 'live';
      default: return 'unknown';
    }
  }

  getStatusLabel(type: number): string {
    switch(type) {
      case SourcingType.Active: return 'Common.Active';
      case SourcingType.External: return 'Common.Passive';
      case SourcingType.Passive: return 'Analytics.LiveBadge';
      default: return '';
    }
  }

  private buildCandidateCompensationRows(item): { labelKey: string; shortLabelKey: string; value: string; isMissing: boolean; isUnverified: boolean }[] {
    const talent = item?.talent || {};
    const currentSalary = this.salaryService.format(talent.salary);
    const expectedSalary = this.salaryService.format(talent.expectedSalary);
    const noticePeriod = this.formatNoticePeriod(talent.noticePeriod);

    return [
      { labelKey: 'MatchItem.CurrentSalary', shortLabelKey: 'MatchItem.CurrentSalaryShort', value: currentSalary || 'N/A', isMissing: !currentSalary, isUnverified: this.salaryService.isUnverified(talent.salary) },
      { labelKey: 'MatchItem.ExpectedSalary', shortLabelKey: 'MatchItem.ExpectedSalaryShort', value: expectedSalary || 'N/A', isMissing: !expectedSalary, isUnverified: this.salaryService.isUnverified(talent.expectedSalary) },
      { labelKey: 'MatchItem.NoticePeriod', shortLabelKey: 'MatchItem.NoticePeriodShort', value: noticePeriod || 'N/A', isMissing: !noticePeriod, isUnverified: false },
    ];
  }

  private formatNoticePeriod(value): string | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (typeof value === 'object') {
      const nestedValue = value.value ?? value.days ?? value.amount ?? value.label ?? value.name ?? null;
      return this.formatNoticePeriod(nestedValue);
    }

    if (typeof value === 'number') {
      return `${this.formatNumber(value)} ${value === 1 ? 'day' : 'days'}`;
    }

    const text = String(value).trim();
    if (!text) {
      return null;
    }

    if (/^\d+$/.test(text)) {
      return this.formatNoticePeriod(Number(text));
    }

    return text;
  }

  private formatNumber(value): string {
    const numericValue = Number(value);
    if (Number.isFinite(numericValue)) {
      return numericValue.toLocaleString('en-US');
    }
    return String(value).trim();
  }

  isActionLoading(action: 'like' | 'archive' | 'unarchive' | 'download' | 'verify'): boolean {
    return !!this.candidateId
      && this.processingActionCandidateId === this.candidateId
      && this.processingActionType === action;
  }

  verifyUan(event: Event): void {
    event.stopPropagation();
    if (!this.canVerifyUan || this.isProcessingAction) {
      return;
    }

    this.onVerifyUan.emit(this.candidateId);
  }

  get hasValidWhatsappNumber(): boolean {
    const phone = this.item?.talent?.phone?.phoneNumber;
    return !!phone && !phone.includes('[anonymous]');
  }

  openWhatsappPopup(event: Event): void {
    event.stopPropagation();
    if (!this.hasValidWhatsappNumber) {
      return;
    }

    this.whatsappMessage = this.translate.instant('WhatsApp.DefaultMessage', {
      candidateName: this.item?.talent?.name || '',
      jobTitle: this.getWhatsappJobTitle(),
    });
    this.showWhatsappPopup = true;
  }

  closeWhatsappPopup(event?: Event): void {
    event?.stopPropagation();
    this.contactRequest?.unsubscribe();
    this.revealingPhone = false;
    this.showWhatsappPopup = false;
  }

  openWhatsapp(event: Event): void {
    event.stopPropagation();
    if (this.revealingPhone || !this.showWhatsappPopup) return;
    if (this.item?.talent?.isPhoneNumberViewed === false) {
      const target = this.item;
      const vacancyId = target.vacancy?.id || this.vacancyId;
      if (!vacancyId || !target.talent?.id) return;
      const message = this.whatsappMessage;
      this.revealingPhone = true;
      this.contactError = '';
      this.contactRequest = this.contacts.view(vacancyId, target.talent.id, 'whatsapp').subscribe({
        next: contact => {
          if (this.item !== target) return;
          this._item = { ...target, talent: { ...target.talent, isPhoneNumberViewed: true,
            phone: { countryCode: contact.countryCode, phoneNumber: contact.value } } };
          this.revealingPhone = false;
          const phone = this.getWhatsappPhoneNumber();
          if (!phone) {
            this.contactError = 'This phone number cannot be opened in WhatsApp.';
            return;
          }
          if (this.showWhatsappPopup) {
            getWindow().open(buildWhatsappUrl(phone, message), '_blank', 'noopener');
            this.showWhatsappPopup = false;
          }
        },
        error: () => {
          if (this.item !== target) return;
          this.revealingPhone = false;
          this.contactError = 'Could not reveal phone number. Please try again.';
        },
      });
      return;
    }
    const phoneNumber = this.getWhatsappPhoneNumber();
    if (!phoneNumber) {
      this.showWhatsappPopup = false;
      return;
    }
    getWindow().open(buildWhatsappUrl(phoneNumber, this.whatsappMessage), '_blank', 'noopener');
    this.showWhatsappPopup = false;
  }

  onSocialClick(event: Event, eventType: CandidateProfileEventType): void {
    event.stopPropagation();
    this.onAnalyticsEvent.emit({ eventType, item: this.item });
  }

  showMatch(e) {
    e.stopPropagation();
    this.matchId = this.item.id;
  }

  onMatchDetailsAnalyticsEvent(eventType: CandidateProfileEventType): void {
    this.onAnalyticsEvent.emit({ eventType, item: this.item });
  }

  goInternalVacancy(url) {
    this.gaService.gtag('event', GA_EVENTS.ExternalVcancyApply, { [GA_PROPS.Url]: url });
    getWindow().open(url, '_blank');
  }

  get scoreShadeClass(): string {
    const score = this.index;
    if (score <= 15) return 'shade-deep-green';
    if (score <= 35) return 'shade-light-green';
    return 'shade-lighter-green';
  }

  onThumbUp(e: Event) {
    e.stopPropagation();
    if (this.isProcessingAction) {
      return;
    }
    if (this.role === USER_ROLES.hm && this.item?.talent?.id) {
      const isCurrentlyLiked = this.item.isLiked || false;
      
      // Trigger confetti when liking (not unliking)
      if (!isCurrentlyLiked) {
        this.triggerConfetti(e);
      }
      
      this.onLikeToggle.emit({ 
        candidateId: this.item.talent.id, 
        isLiked: !isCurrentlyLiked 
      });
    }
  }

  triggerConfetti(e: Event) {
    this.showConfetti = true;
    
    // Create confetti elements
    const target = e.target as HTMLElement;
    const rect = target.getBoundingClientRect();
    const confettiContainer = document.createElement('div');
    confettiContainer.className = 'confetti-container';
    confettiContainer.style.position = 'fixed';
    confettiContainer.style.left = rect.left + rect.width / 2 + 'px';
    confettiContainer.style.top = rect.top + rect.height / 2 + 'px';
    confettiContainer.style.pointerEvents = 'none';
    confettiContainer.style.zIndex = '9999';
    
    document.body.appendChild(confettiContainer);
    
    // Create multiple confetti pieces
    const colors = ['#4285f4', '#34a853', '#fbbc04', '#ea4335', '#ff6d00', '#00bcd4'];
    const confettiCount = 30;
    
    for (let i = 0; i < confettiCount; i++) {
      const confetti = document.createElement('div');
      confetti.className = 'confetti-piece';
      confetti.style.position = 'absolute';
      confetti.style.width = '10px';
      confetti.style.height = '10px';
      confetti.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
      confetti.style.left = '0';
      confetti.style.top = '0';
      confetti.style.borderRadius = Math.random() > 0.5 ? '50%' : '0';
      
      const angle = (Math.PI * 2 * i) / confettiCount;
      const velocity = 100 + Math.random() * 100;
      const tx = Math.cos(angle) * velocity;
      const ty = Math.sin(angle) * velocity - 50;
      
      confetti.style.animation = `confetti-fall-${i} 1s ease-out forwards`;
      
      const keyframes = `
        @keyframes confetti-fall-${i} {
          0% {
            transform: translate(0, 0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translate(${tx}px, ${ty + 200}px) rotate(${Math.random() * 720}deg);
            opacity: 0;
          }
        }
      `;
      
      const style = document.createElement('style');
      style.textContent = keyframes;
      document.head.appendChild(style);
      
      confettiContainer.appendChild(confetti);
      
      setTimeout(() => {
        style.remove();
      }, 1000);
    }
    
    // Clean up after animation
    setTimeout(() => {
      confettiContainer.remove();
      this.showConfetti = false;
    }, 1000);
  }

  onThumbDown(e: Event) {
    e.stopPropagation();
    if (this.isProcessingAction) {
      return;
    }
    if (this.role === USER_ROLES.hm && this.item?.talent?.id) {
      this.onArchive.emit(this.item.talent.id);
    }
  }

  onUnarchiveClick(e: Event) {
    e.stopPropagation();
    if (this.isProcessingAction) {
      return;
    }
    if (this.role === USER_ROLES.hm && this.item?.talent?.id) {
      this.onUnarchive.emit(this.item.talent.id);
    }
  }

  getLinkedInHandle(url: string): string {
    if (!url) {
      return '';
    }

    const lower = url.toLowerCase();
    const prefixes = [
      'https://www.linkedin.com/in/',
      'http://www.linkedin.com/in/',
      'https://linkedin.com/in/',
      'http://linkedin.com/in/',
      'www.linkedin.com/in/',
      'linkedin.com/in/'
    ];

    let handle = url;
    for (const prefix of prefixes) {
      if (lower.startsWith(prefix)) {
        handle = url.substring(prefix.length);
        break;
      }
    }

    handle = handle.replace(/\/+$/, '');

    return handle ? `@${handle}` : '';
  }

  private getWhatsappPhoneNumber(): string | null {
    const talent = this.item?.talent || {};
    return getWhatsappPhoneNumber({
      phoneNumber: talent.phone?.phoneNumber || talent.phoneNumber || talent.phone,
      countryCode: talent.phone?.countryCode || talent.countryCode || talent.phoneCode,
      country: talent.location?.country || talent.country,
    });
  }

  private getWhatsappJobTitle(): string {
    return this.vacancyTitle || '';
  }
}
